import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';
import SEOMeta from './SEOMeta';
import { fuzzySearchPlants } from './plantSearchUtils';
import { slugify } from './slugUtils';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [plantCount, setPlantCount] = useState(0);
  const [totalSales, setTotalSales] = useState(0);
  const [tcCount, setTcCount] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [allNames, setAllNames] = useState([]);
  const [plantRecords, setPlantRecords] = useState([]);
  const [scientificMap, setScientificMap] = useState({});
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [heroPhotos, setHeroPhotos] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [featuredPlants, setFeaturedPlants] = useState([]);
  const [recentSightings, setRecentSightings] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    document.title = 'True Plant Value - Real Sold Prices for Rare Houseplants';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', 'Search real sold prices for rare and common houseplants from actual plant collectors. Free, community-driven plant price database.');
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      let data = [];
      let hFrom = 0;
      while (true) {
        const { data: pageData } = await supabase.from('plants').select('common_name, scientific_name, sale_type').eq('status', 'approved').range(hFrom, hFrom + 999);
        if (!pageData || pageData.length === 0) break;
        data = data.concat(pageData);
        if (pageData.length < 1000) break;
        hFrom += 1000;
      }
      if (data.length > 0) {
        const names = [...new Set(data.map(p => p.common_name?.trim()).filter(Boolean))].sort();
        setAllNames(names);
        setPlantRecords(data);
        setPlantCount(names.length);
        setTotalSales(data.length);
        const tc = data.filter(p => p.sale_type && (p.sale_type.toLowerCase().includes('tissue') || p.sale_type.toUpperCase() === 'TC')).length;
        setTcCount(tc);
        const sciMap = {};
        data.forEach(p => {
          if (p.scientific_name && p.common_name) sciMap[p.scientific_name.toLowerCase().trim()] = p.common_name.trim();
        });
        setScientificMap(sciMap);
      }
    };

    const fetchHeroPhotos = async () => {
      const { data } = await supabase.from('hero_photos').select('*').eq('active', true).order('display_order', { ascending: true });
      if (data && data.length > 0) setHeroPhotos(data);
    };

    const fetchFeatured = async () => {
      // Fetch member sold plants with photos
      const { data: inventoryData } = await supabase
        .from('inventory')
        .select('common_name, price, image_url, genus, date_sold, created_at')
        .eq('status', 'sold')
        .not('image_url', 'is', null)
        .not('image_url', 'eq', '')
        .order('date_sold', { ascending: false })
        .limit(20);

      // Fetch community plant submissions with photos
      const { data: plantsData } = await supabase
        .from('plants')
        .select('id, common_name, sold_price, image_url, genus, created_at')
        .eq('status', 'approved')
        .not('image_url', 'is', null)
        .not('image_url', 'eq', '')
        .order('id', { ascending: false })
        .limit(50);

      // Combine with a unified "recency" timestamp so true newest-first ordering works
      // regardless of which source (member sale or community submission) it came from
      const combined = [
        ...(inventoryData || []).map(p => ({
          common_name: p.common_name,
          sold_price: p.price,
          image_url: p.image_url,
          genus: p.genus,
          _recency: p.created_at || p.date_sold || 0,
        })),
        ...(plantsData || []).map(p => ({
          common_name: p.common_name,
          sold_price: p.sold_price,
          image_url: p.image_url,
          genus: p.genus,
          _recency: p.created_at || p.id || 0,
        })),
      ];

      // Sort by true recency (newest first), regardless of source
      combined.sort((a, b) => {
        const aTime = typeof a._recency === 'number' ? a._recency : new Date(a._recency).getTime();
        const bTime = typeof b._recency === 'number' ? b._recency : new Date(b._recency).getTime();
        return (bTime || 0) - (aTime || 0);
      });

      // Deduplicate by common_name — first occurrence wins (most recent)
      const seen = new Set();
      const unique = combined.filter(p => {
        if (!p.common_name || seen.has(p.common_name.toLowerCase())) return false;
        seen.add(p.common_name.toLowerCase());
        return true;
      }).slice(0, 4);

      setFeaturedPlants(unique);
      setFeaturedLoading(false);
    };

    const fetchRecentSightings = async () => {
      const { data } = await supabase.from('store_sightings').select('*').eq('status', 'approved').order('date_seen', { ascending: false }).limit(3);
      setRecentSightings(data || []);
    };

    const fetchRecentPosts = async () => {
      const { data } = await supabase.from('blog_posts').select('*').eq('status', 'published').order('created_at', { ascending: false }).limit(3);
      setRecentPosts(data || []);
    };

    const fetchRecentSales = async () => {
      const { data } = await supabase.from('plants').select('common_name, sold_price, date_sold, state, sale_type, genus, image_url').eq('status', 'approved').not('image_url', 'is', null).not('image_url', 'eq', '').order('id', { ascending: false }).limit(4);
      setRecentSales(data || []);
    };

    fetchData();
    fetchHeroPhotos();
    fetchFeatured();
    fetchRecentSightings();
    fetchRecentPosts();
    fetchRecentSales();

    // Auto-refresh featured plants and recent sales every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchFeatured();
      fetchRecentSales();
    }, 5 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    if (heroPhotos.length <= 1) return;
    const interval = setInterval(() => setCurrentPhotoIndex(i => (i + 1) % heroPhotos.length), 5000);
    return () => clearInterval(interval);
  }, [heroPhotos]);

  // Round down to nearest 100 for the stats display
  const roundedSales = Math.floor(totalSales / 100) * 100;

  const handleSearch = (query, navigateDirect = false) => {
    const q = (query || searchQuery).trim();
    if (!q) { navigate('/search'); return; }
    if (navigateDirect) {
      const exactMatch = allNames.find(n => n.toLowerCase() === q.toLowerCase());
      if (exactMatch) { navigate(`/plant/${slugify(exactMatch)}`); return; }
      const sciMatch = scientificMap[q.toLowerCase()];
      if (sciMatch) { navigate(`/plant/${slugify(sciMatch)}`); return; }
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setHighlightedIndex(-1);
    if (val.length > 0) {
      const matches = fuzzySearchPlants(val, plantRecords, 8);
      setSuggestions(matches.map(m => m.common_name));
      setShowSuggestions(true);
    } else { setSuggestions([]); setShowSuggestions(false); }
  };

  const formatPrice = (val) => {
    if (!val) return '—';
    const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
    return isNaN(num) ? val : '$' + num.toFixed(2);
  };

  return (
    <div style={{ paddingBottom: '80px' }}>
      <SEOMeta
        title="Real Sold Prices for Rare Houseplants"
        description="Search real sold prices from actual plant collectors. Find what Monstera, Alocasia, Philodendron, and rare houseplants actually sell for. Free community-driven price database."
        url="/"
      />

      {/* HERO */}
      <div style={{
        position: 'relative',
        minHeight: isMobile ? '85vh' : '90vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', overflow: 'hidden',
        background: 'linear-gradient(135deg, #1e3d20 0%, #2c4a2e 60%, #4a7c59 100%)',
        marginBottom: '0',
      }}>
        {heroPhotos.map((photo, idx) => (
          <div key={photo.id} style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            backgroundImage: `url(${photo.image_url})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
            opacity: idx === currentPhotoIndex ? 1 : 0,
            transition: 'opacity 1.5s ease-in-out', zIndex: 0,
          }} />
        ))}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,32,15,0.65)', zIndex: 1 }} />

        <div style={{ position: 'relative', zIndex: 2, padding: '40px 24px', maxWidth: '780px', width: '100%' }}>
          <div style={{ fontSize: '11px', letterSpacing: '6px', color: '#8fb99a', textTransform: 'uppercase', marginBottom: '20px', fontFamily: 'Lato, sans-serif', fontWeight: 300 }}>
            🌿 Real Data. Real Prices.
          </div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '38px' : '64px', color: '#f5f0e8', fontWeight: 700, lineHeight: 1.1, marginBottom: '20px' }}>
            Know What Your Plants Are Worth
          </h1>
          <p style={{ color: 'rgba(245,240,232,0.8)', fontFamily: 'Lato, sans-serif', fontSize: isMobile ? '16px' : '20px', fontWeight: 300, maxWidth: '520px', margin: '0 auto 40px', lineHeight: 1.7 }}>
            Search real sold prices from actual plant collectors. Stop guessing, start knowing.
          </p>

          <div style={{ maxWidth: '600px', margin: '0 auto', position: 'relative' }}>
            <div style={{ display: 'flex', background: '#fff', borderRadius: '16px', overflow: 'visible', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={() => searchQuery && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => { setShowSuggestions(false); setHighlightedIndex(-1); }, 150)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIndex(i => Math.min(i + 1, suggestions.length - 1)); }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIndex(i => Math.max(i - 1, -1)); }
                  else if (e.key === 'Enter') {
                    if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
                      const val = suggestions[highlightedIndex];
                      setSearchQuery(val); setShowSuggestions(false); setHighlightedIndex(-1);
                      handleSearch(val, true);
                    } else { handleSearch(searchQuery, false); }
                  }
                  else if (e.key === 'Escape') { setShowSuggestions(false); setHighlightedIndex(-1); }
                }}
                placeholder="Search any plant... e.g. Monstera Albo"
                style={{ flex: 1, padding: '20px 24px', border: 'none', outline: 'none', fontSize: '16px', fontFamily: 'Lato, sans-serif', color: '#1e2d1f', borderRadius: '16px 0 0 16px', background: 'transparent' }}
              />
              <button onClick={() => handleSearch(searchQuery, false)}
                style={{ padding: '20px 32px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', border: 'none', color: '#f5f0e8', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif', borderRadius: '0 16px 16px 0', whiteSpace: 'nowrap' }}>
                Search
              </button>
            </div>

            {showSuggestions && suggestions.length > 0 && (
              <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '2px solid #4a7c59', borderRadius: '12px', listStyle: 'none', margin: '4px 0 0', padding: '6px', zIndex: 1000, maxHeight: '220px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(44,74,46,0.15)', textAlign: 'left' }}>
                {suggestions.map((name, idx) => (
                  <li key={name}
                    onMouseDown={() => { setSearchQuery(name); setShowSuggestions(false); setHighlightedIndex(-1); handleSearch(name, true); }}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderRadius: '8px', color: '#1e2d1f', fontSize: '14px', fontFamily: 'Lato, sans-serif', background: highlightedIndex === idx ? '#f0f7f1' : 'transparent', fontWeight: highlightedIndex === idx ? 700 : 400 }}>
                    {name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p style={{ color: 'rgba(245,240,232,0.5)', fontFamily: 'Lato, sans-serif', fontSize: '13px', marginTop: '16px' }}>
            or <span style={{ textDecoration: 'underline', cursor: 'pointer', color: 'rgba(245,240,232,0.7)' }} onClick={() => navigate('/search')}>browse all {plantCount} plants →</span>
          </p>
        </div>

        {heroPhotos.length > 1 && (
          <div style={{ position: 'absolute', bottom: '24px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px', zIndex: 3 }}>
            {heroPhotos.map((_, idx) => (
              <div key={idx} onClick={() => setCurrentPhotoIndex(idx)}
                style={{ width: '8px', height: '8px', borderRadius: '50%', background: idx === currentPhotoIndex ? '#f5f0e8' : 'rgba(245,240,232,0.35)', cursor: 'pointer', transition: 'all 0.3s' }} />
            ))}
          </div>
        )}
        <div style={{ position: 'absolute', bottom: '24px', right: '24px', zIndex: 3, color: 'rgba(245,240,232,0.4)', fontFamily: 'Lato, sans-serif', fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase' }}>
          Scroll ↓
        </div>
      </div>

      {/* STATS BAR */}
      <div style={{ background: '#f7f3ec', padding: '40px 24px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
          {[
            { number: roundedSales.toLocaleString() + '+', label: 'Sales Recorded', icon: '📊', desc: 'Real transactions from collectors', link: '/search', cta: 'Browse all sales →' },
            { number: plantCount + '+', label: 'Plant Varieties', icon: '🌱', desc: 'Species with price history', link: '/plants', cta: 'Search plants →' },
            { number: tcCount + '+', label: 'Tissue Culture', icon: '🧫', desc: 'TC plant transactions tracked', link: '/tissue-culture', cta: 'See TC plants →' },
            { number: '100%', label: 'Free to Search', icon: '💚', desc: 'No account needed, ever', link: '/about', cta: 'Learn more →' },
          ].map(({ number, label, icon, desc, link, cta }) => (
            <div key={label} onClick={() => navigate(link)}
              style={{ background: '#ffffff', borderRadius: '20px', padding: '24px 20px', textAlign: 'center', border: '1px solid rgba(143,185,154,0.25)', cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(44,74,46,0.15)'; e.currentTarget.style.borderColor = '#4a7c59'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'rgba(143,185,154,0.25)'; }}
            >
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{icon}</div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '34px', color: '#1e3d20', fontWeight: 700, lineHeight: 1 }}>{number}</div>
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '11px', color: '#4a7c59', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '8px', fontWeight: 700 }}>{label}</div>
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#8b9e8c', marginTop: '4px' }}>{desc}</div>
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#4a7c59', marginTop: '12px', fontWeight: 700 }}>{cta}</div>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURED PLANTS — includes member sold plants with photos */}
      {featuredPlants.length > 0 && (
        <div style={{ padding: '60px 24px', maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#4a7c59', textTransform: 'uppercase', marginBottom: '8px', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>From Our Database</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '36px', color: '#1e3d20' }}>Recently Tracked Plants</h2>
          </div>
          {/* Skeleton CSS */}
          <style>{`
            @keyframes shimmer {
              0% { background-position: -400px 0; }
              100% { background-position: 400px 0; }
            }
            .skeleton {
              background: linear-gradient(90deg, #e8f0e9 25%, #d4e6d6 50%, #e8f0e9 75%);
              background-size: 400px 100%;
              animation: shimmer 1.4s ease-in-out infinite;
              border-radius: 8px;
            }
          `}</style>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '20px' }}>
            {featuredLoading ? (
              [1,2,3,4].map(i => (
                <div key={i} style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(44,74,46,0.08)', background: '#fff' }}>
                  <div className="skeleton" style={{ height: '180px', borderRadius: '0' }} />
                  <div style={{ padding: '14px 16px' }}>
                    <div className="skeleton" style={{ height: '12px', width: '40%', marginBottom: '10px' }} />
                    <div className="skeleton" style={{ height: '16px', width: '80%', marginBottom: '6px' }} />
                    <div className="skeleton" style={{ height: '14px', width: '55%' }} />
                  </div>
                </div>
              ))
            ) : featuredPlants.map((plant) => (
              <div key={plant.common_name} onClick={() => navigate(`/plant/${slugify(plant.common_name)}`)}
                style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(44,74,46,0.12)', cursor: 'pointer', background: '#fff', transition: 'transform 0.2s, box-shadow 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(44,74,46,0.2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(44,74,46,0.12)'; }}
              >
                <div style={{ height: '180px', backgroundImage: `url(${plant.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                  <div style={{ position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(30,61,32,0.85)', color: '#f5f0e8', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, fontFamily: 'Lato, sans-serif' }}>
                    {formatPrice(plant.sold_price)}
                  </div>
                </div>
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ fontSize: '10px', color: '#8b6914', fontFamily: 'Lato, sans-serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>{plant.genus || 'Plant'}</div>
                  <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '15px', color: '#1e3d20', margin: 0, lineHeight: 1.3 }}>{plant.common_name}</h3>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <button onClick={() => navigate('/search')} style={{ background: 'transparent', border: '2px solid #4a7c59', color: '#4a7c59', borderRadius: '24px', padding: '12px 32px', fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              Browse All {plantCount} Plants →
            </button>
          </div>
        </div>
      )}

      {/* FOR SALE BY MEMBERS CTA */}
      <div style={{ background: '#f7f3ec', padding: '40px 24px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'space-between', gap: '24px', background: '#fff', borderRadius: '20px', padding: '32px', boxShadow: '0 2px 16px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.3)' }}>
          <div>
            <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#8b6914', textTransform: 'uppercase', marginBottom: '8px', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>🌿 Member Listings</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '24px' : '30px', color: '#1e3d20', marginBottom: '8px' }}>Plants Available for Sale</h2>
            <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', color: '#4a5e4b', lineHeight: 1.6, maxWidth: '480px' }}>
              Browse rare and common plants listed for sale by our community members. Filter by genus, type, or search by name.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', flexShrink: 0 }}>
            <button onClick={() => navigate('/for-sale')} style={{ background: 'linear-gradient(135deg, #8b6914, #c4a882)', color: '#f5f0e8', border: 'none', borderRadius: '24px', padding: '14px 28px', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif', whiteSpace: 'nowrap' }}>
              Browse Plants for Sale →
            </button>
            <button onClick={() => navigate('/seller/login')} style={{ background: 'transparent', border: '2px solid #8b6914', color: '#8b6914', borderRadius: '24px', padding: '14px 28px', fontSize: '14px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif', whiteSpace: 'nowrap' }}>
              List Your Plants
            </button>
          </div>
        </div>
      </div>

      {/* RECENTLY SOLD */}
      {recentSales.length > 0 && (
        <div style={{ background: '#f0f7f1', padding: '60px 24px' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#4a7c59', textTransform: 'uppercase', marginBottom: '8px', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>Live Market Data</div>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '36px', color: '#1e3d20' }}>Recently Sold</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '20px' }}>
              {recentSales.map((sale, i) => (
                <div key={i} onClick={() => navigate(`/plant/${slugify(sale.common_name)}`)}
                  style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(44,74,46,0.12)', cursor: 'pointer', background: '#fff', transition: 'transform 0.2s, box-shadow 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(44,74,46,0.2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(44,74,46,0.12)'; }}
                >
                  <div style={{ height: '180px', backgroundImage: `url(${sale.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                    <div style={{ position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(30,61,32,0.85)', color: '#f5f0e8', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, fontFamily: 'Lato, sans-serif' }}>
                      {formatPrice(sale.sold_price)}
                    </div>
                  </div>
                  <div style={{ padding: '14px 16px' }}>
                    <div style={{ fontSize: '10px', color: '#8b6914', fontFamily: 'Lato, sans-serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>{sale.genus || sale.sale_type || 'Plant'}</div>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '15px', color: '#1e3d20', margin: 0, lineHeight: 1.3 }}>{sale.common_name}</h3>
                    <div style={{ fontSize: '11px', color: '#8b9e8c', fontFamily: 'Lato, sans-serif', marginTop: '6px' }}>{sale.state || 'USA'} · {sale.date_sold ? new Date(sale.date_sold).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* COMMUNITY FINDS — Spotted in the Wild */}
      {recentSightings.length > 0 && (
        <div style={{ padding: '60px 24px', maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#8b6914', textTransform: 'uppercase', marginBottom: '8px', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>Community Finds</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '36px', color: '#1e3d20' }}>Spotted in the Wild 🔍</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '20px' }}>
            {recentSightings.map((s) => (
              <div key={s.id} style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(139,105,20,0.1)', border: '1px solid rgba(139,105,20,0.15)' }}>
                <div style={{ padding: '16px' }}>
                  <div style={{ fontSize: '11px', color: '#8b6914', fontFamily: 'Lato, sans-serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>🏪 {s.store || 'Store'}</div>
                  <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '17px', color: '#1e3d20', marginBottom: '8px' }}>{s.common_name}</h3>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b' }}>{[s.city, s.state].filter(Boolean).join(', ')}</span>
                    <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#8b6914', fontWeight: 700 }}>{s.price ? `$${parseFloat(String(s.price).replace(/[^0-9.]/g, '')).toFixed(2)}` : ''}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <button onClick={() => navigate('/spotted')} style={{ background: 'transparent', border: '2px solid #8b6914', color: '#8b6914', borderRadius: '24px', padding: '12px 32px', fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              Submit a Sighting →
            </button>
          </div>
        </div>
      )}

      {/* FROM THE BLOG */}
      {recentPosts.length > 0 && (
        <div style={{ background: '#f7f3ec', padding: '60px 24px' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#4a7c59', textTransform: 'uppercase', marginBottom: '8px', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>Plant Price Insights</div>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '36px', color: '#1e3d20' }}>From the Blog</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '24px' }}>
              {recentPosts.map((post) => (
                <div key={post.id} onClick={() => navigate(`/blog/${post.slug}`)}
                  style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(44,74,46,0.08)', cursor: 'pointer', transition: 'transform 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {post.image_url ? (
                    <img src={post.image_url} alt={post.title} loading="lazy" style={{ width: '100%', height: '180px', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ height: '180px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '56px' }}>{post.emoji || '🌿'}</div>
                  )}
                  <div style={{ padding: '20px' }}>
                    {post.category && (
                      <span style={{ background: '#f0f7f1', color: '#4a7c59', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'Lato, sans-serif' }}>{post.category}</span>
                    )}
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e3d20', margin: '10px 0 8px', lineHeight: 1.3 }}>{post.title}</h3>
                    {post.subtitle && (
                      <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', margin: 0, lineHeight: 1.6 }}>{post.subtitle.slice(0, 90)}{post.subtitle.length > 90 ? '...' : ''}</p>
                    )}
                    <div style={{ marginTop: '12px', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontSize: '13px', fontWeight: 700 }}>Read more →</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <button onClick={() => navigate('/blog')} style={{ background: 'transparent', border: '2px solid #4a7c59', color: '#4a7c59', borderRadius: '24px', padding: '12px 32px', fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
                View All Posts →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HOW IT WORKS */}
      <div style={{ padding: '60px 24px', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ fontSize: '11px', letterSpacing: '4px', color: '#4a7c59', textTransform: 'uppercase', marginBottom: '12px', fontFamily: 'Lato, sans-serif', fontWeight: 700 }}>Simple &amp; Free</div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '36px', color: '#1e3d20' }}>How It Works</h2>
        </div>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {[
            { num: '01', icon: '🔍', title: 'Search Any Plant', desc: 'Type in any rare or common houseplant and instantly see real sold prices from actual collectors.' },
            { num: '02', icon: '📊', title: 'See Real Data', desc: 'View price history charts, breakdowns by size, and tissue culture pack pricing, all from verified sales.' },
            { num: '03', icon: '🌿', title: 'Buy & Sell Smarter', desc: 'Use real market data to price your plants fairly or know exactly what to pay before you buy.' },
          ].map((step) => (
            <div key={step.num} style={{ flex: '1 1 0', minWidth: '240px', padding: '32px 24px', background: '#fff', borderRadius: '20px', boxShadow: '0 2px 16px rgba(44,74,46,0.08)', border: '1px solid rgba(143,185,154,0.3)' }}>
              <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontWeight: 700, marginBottom: '12px' }}>{step.num}</div>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>{step.icon}</div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', marginBottom: '10px' }}>{step.title}</h3>
              <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', color: '#4a5e4b', lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* HELP GROW THE DATA — CTA */}
      <div style={{ padding: '0 24px 60px', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e3d20 0%, #2c4a2e 60%, #4a7c59 100%)', borderRadius: '24px', padding: '40px 24px', textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', color: '#f5f0e8', marginBottom: '16px' }}>Help Grow the Data</h2>
          <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '16px', color: '#8fb99a', margin: '0 auto 32px', maxWidth: '480px', lineHeight: 1.7 }}>
            Every sale you submit makes the platform more valuable for the entire plant community. It takes 60 seconds.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/submit')} style={{ background: '#f5f0e8', color: '#1e3d20', border: 'none', borderRadius: '24px', padding: '14px 36px', fontSize: '14px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              Submit a Sale
            </button>
            <button onClick={() => navigate('/about')} style={{ background: 'transparent', color: '#f5f0e8', border: '2px solid rgba(245,240,232,0.4)', borderRadius: '24px', padding: '14px 36px', fontSize: '14px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              Our Story
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;